import React from 'react';
import {
  Archive, Armchair, Bank, Bathtub, Car, Certificate, ChartBarHorizontal, ChatCircle, Check, ClockCounterClockwise, CookingPot, CrownSimple,
  CurrencyEur, Elevator, EnvelopeSimple, EnvelopeSimpleOpen, FolderSimple, Gauge, HouseSimple, Key, MapPinArea, NotePencil, Package, Phone,
  PiggyBank, Plant, Rows, Ruler, ShieldCheck, Signature, Sun, SunHorizon, Thermometer, Ticket, Toilet, TrendUp, Umbrella, User, UsersThree,
  Vault, Wallet, WashingMachine, type Icon,
} from '@phosphor-icons/react';
import { Chip, Widget, WidgetWert, type WidgetProps } from '../../../../components/immobilien/ui';
import { ANGEBUNDENE_BEREICHE, type EinheitWidgetDef } from '../../../../components/immobilien/einheitWidgets';
import { eur, initialen, qm } from '../../../../lib/immobilien/format';
import { deDatum } from '../../../../lib/immobilien/objektDetail';
import { ausstattungListe, kaufKennzahlen, kautionInfo, zaehlerDerEinheit, type KautionStatus } from '../../../../lib/immobilien/einheitDetail';
import { BUCHHALTUNG_FOLGT } from '../../../../lib/immobilien/hinweise';
import { usePortalStatus } from '../../../../lib/immobilien/usePortalStatus';
import { kontaktName } from '../../../../lib/objekte/objektKontakte';
import { Folgt, Leer, RUND } from '../objekt/ObjektWidgets';
import { useEinheitSeite } from './EinheitSeitenKontext';
import { analyseMieterhoehung } from '../../../../lib/immobilien/mieterhoehung';
import { schluesselJeEinheit } from '../../../../lib/immobilien/schluessel';
import { baueAktivitaeten } from '../../../../lib/immobilien/aktivitaet';
import { istTicketOffen, ticketStatusLabel } from '../../../../lib/immobilien/tickets';

/** Icons für die Ausstattungsmerkmale des Einheiten-Formulars; Unbekanntes bekommt einen Haken. */
export const MERKMAL_ICON: Record<string, Icon> = {
  'Einbauküche': CookingPot, 'Keller': Package, 'Gäste WC': Toilet, 'Wohnberechtigungsschein': Certificate, 'Aufzug': Elevator,
  'Garten/-Mitbenutzung': Plant, 'WG geeignet': UsersThree, 'Garage / Stellplatz': Car, 'Balkon': Sun, 'Terrasse': SunHorizon,
  'Fußbodenheizung': Thermometer, 'Dachboden': HouseSimple, 'Abstellraum': Archive, 'Waschmaschinenanschluss': WashingMachine,
  'Rollläden': Rows, 'Badewanne': Bathtub,
};

export const KAUTION_CHIP: Record<KautionStatus, { ton: 'ok' | 'warn' | 'bad' | 'neutral'; text: string }> = {
  hinterlegt: { ton: 'ok', text: 'Hinterlegt' },
  teilweise: { ton: 'warn', text: 'Teilweise' },
  offen: { ton: 'bad', text: 'Offen' },
  keine: { ton: 'neutral', text: 'Keine Kaution' },
};

const betrag = (x: number) => eur(x, { nachkomma: Number.isInteger(x) ? 0 : 2 });

/** Kleiner Knopf im Widget – stoppt den Klick, damit das Widget nicht gleichzeitig öffnet. */
function WidgetKnopf({ icon: IconKomp, children, ton = 'neutral', href, onClick, deaktiviert, titel }: {
  icon: Icon; children: React.ReactNode; ton?: 'neutral' | 'ok'; href?: string; onClick?: () => void; deaktiviert?: boolean; titel?: string;
}) {
  const stil: React.CSSProperties = {
    flex: 1, minWidth: 0, height: 36, borderRadius: 12, border: 'none', gap: 6, fontSize: 13, fontWeight: 600, textDecoration: 'none',
    background: ton === 'ok' ? 'var(--ok-soft)' : 'var(--fill2)', color: ton === 'ok' ? 'var(--ok)' : 'var(--ink)', opacity: deaktiviert ? 0.45 : 1,
  };
  const inhalt = <><IconKomp weight="fill" size={14} /><span className="truncate">{children}</span></>;
  const stopp = (e: React.SyntheticEvent) => e.stopPropagation();
  if (href && !deaktiviert) {
    return <a href={href} onClick={stopp} onKeyDown={stopp} title={titel} className="immo-hover-flaeche flex items-center justify-center" style={stil}>{inhalt}</a>;
  }
  return (
    <button type="button" disabled={deaktiviert} title={titel} onKeyDown={stopp}
      onClick={e => { e.stopPropagation(); onClick?.(); }}
      className={`flex items-center justify-center ${deaktiviert ? 'cursor-default' : 'immo-hover-flaeche cursor-pointer'}`} style={stil}>
      {inhalt}
    </button>
  );
}

/**
 * Ein Einheit-Widget nach seinem Schlüssel.
 * gesperrt: Klick öffnet nichts (Bearbeiten-Modus). gedimmt: ausgeblendetes Widget im Bearbeiten-Modus.
 */
export function EinheitWidget({ def, gesperrt, gedimmt = false }: { def: EinheitWidgetDef; gesperrt: boolean; gedimmt?: boolean }) {
  const s = useEinheitSeite();
  const { daten, aktuell, miete } = s;
  const e = daten.einheit;
  const angebunden = ANGEBUNDENE_BEREICHE.has(def.key);
  const basis: Pick<WidgetProps, 'titel' | 'groesse' | 'onOeffnen' | 'style'> = {
    titel: def.label,
    groesse: def.groesse,
    onOeffnen: gesperrt || !angebunden ? undefined : () => s.oeffneBereich(def.key),
    style: gedimmt ? { opacity: 0.4 } : undefined,
  };

  switch (def.key) {
    case 'mieter':
      return <MieterWidget basis={basis} gesperrt={gesperrt} />;
    case 'gesamt':
      return (
        <Widget {...basis} icon={CurrencyEur} akzent>
          <WidgetWert akzent wert={aktuell ? eur(miete.gesamt) : '–'} sub={aktuell ? 'pro Monat' : 'Leerstand'} />
        </Widget>
      );
    case 'flaeche': {
      const teile = [e.zimmer ? `${String(e.zimmer).replace('.', ',')} Zimmer` : '', (e.etage ?? '').trim()].filter(Boolean);
      return (
        <Widget {...basis} icon={Ruler}>
          <WidgetWert wert={s.flaeche > 0 ? qm(s.flaeche) : '–'} sub={teile.join(' · ') || 'Keine Angaben'} />
        </Widget>
      );
    }
    case 'ausst': {
      const liste = ausstattungListe(e.ausstattung);
      return (
        <Widget {...basis} icon={Armchair} rechts={liste.length ? `${liste.length} ${liste.length === 1 ? 'Merkmal' : 'Merkmale'}` : undefined}>
          {liste.length === 0 ? <Leer text="Noch keine Ausstattung erfasst." /> : (
            <div className="flex flex-wrap content-start overflow-hidden" style={{ marginTop: 14, gap: 8, minHeight: 0 }}>
              {liste.map(m => {
                const IconKomp = MERKMAL_ICON[m] ?? Check;
                return (
                  <span key={m} className="flex items-center whitespace-nowrap" style={{ height: 30, padding: '0 11px', borderRadius: 15, gap: 6, fontSize: 12, fontWeight: 600, background: 'var(--fill2)' }}>
                    <IconKomp weight="fill" size={13} />{m}
                  </span>
                );
              })}
            </div>
          )}
        </Widget>
      );
    }
    case 'kaution': {
      const k = kautionInfo(aktuell);
      const chip = KAUTION_CHIP[k.status];
      return (
        <Widget {...basis} icon={Vault}>
          <div className="flex flex-col" style={{ marginTop: 'auto', gap: 6 }}>
            <span className="immo-kpi" style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-0.03em' }}>{aktuell && k.soll > 0 ? betrag(k.soll) : '–'}</span>
            <span><Chip groesse="xs" ton={chip.ton}>{aktuell ? (k.status === 'teilweise' ? `${betrag(k.offen)} offen` : chip.text) : 'Kein Mietverhältnis'}</Chip></span>
          </div>
        </Widget>
      );
    }
    case 'mv': {
      const z = s.zeitstrahl;
      return (
        <Widget {...basis} icon={ClockCounterClockwise} rechts={z.length ? `${z.length} ${z.length === 1 ? 'Verhältnis' : 'Verhältnisse'}` : undefined}>
          {z.length === 0 ? <Leer text="Noch kein Mietverhältnis." /> : (
            <div className="relative flex flex-col overflow-hidden" style={{ marginTop: 14, gap: 16, paddingLeft: 26, minHeight: 0 }}>
              <span aria-hidden className="absolute" style={{ left: 6, top: 8, bottom: 8, width: 2, background: 'var(--line)' }} />
              {z.slice(0, 4).map(m => (
                <div key={m.id} className="relative flex flex-col min-w-0" style={{ gap: 2 }}>
                  <span aria-hidden className="absolute" style={{ left: -26, top: 3, width: 14, height: 14, borderRadius: '50%',
                    background: m.status === 'aktiv' ? 'var(--ok)' : m.status === 'gekuendigt' ? 'var(--warn)' : 'var(--ink3)', boxShadow: '0 0 0 3px var(--bg)' }} />
                  <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{m.name}</span>
                  <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>
                    {m.status === 'ehemalig' ? `${deDatum(m.von)} – ${deDatum(m.bis)}` : `seit ${deDatum(m.von)} · ${m.status === 'gekuendigt' ? 'gekündigt' : 'aktiv'}`}
                  </span>
                  <span className="immo-zahl" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink2)' }}>{eur(m.gesamt)} / Monat</span>
                </div>
              ))}
            </div>
          )}
        </Widget>
      );
    }
    case 'zus': {
      const teile = [
        { label: 'Kaltmiete', wert: miete.kalt, farbe: 'var(--acc)' },
        { label: 'Nebenkosten', wert: miete.nk, farbe: 'var(--info)' },
        { label: 'Heizkosten', wert: miete.hk, farbe: 'var(--bad)' },
        { label: 'Weitere', wert: miete.weitere, farbe: 'var(--warn)' },
      ].filter(t => t.wert > 0);
      return (
        <Widget {...basis} icon={ChartBarHorizontal} rechts={aktuell ? eur(miete.gesamt) : undefined}>
          {!aktuell || miete.gesamt <= 0 ? <Leer text={aktuell ? 'Keine Mietbeträge hinterlegt.' : 'Leerstand – keine Miete.'} /> : (
            <div className="flex flex-col" style={{ marginTop: 'auto', gap: 14 }}>
              <div className="flex overflow-hidden" style={{ height: 14, borderRadius: 7, gap: 3 }} role="img" aria-label={teile.map(t => `${t.label} ${eur(t.wert)}`).join(', ')}>
                {teile.map(t => <span key={t.label} style={{ flex: t.wert, background: t.farbe, borderRadius: 7 }} />)}
              </div>
              <div className="grid" style={{ gridTemplateColumns: `repeat(${teile.length},minmax(0,1fr))`, gap: 10 }}>
                {teile.map(t => (
                  <div key={t.label} className="flex flex-col min-w-0" style={{ gap: 2 }}>
                    <span className="flex items-center truncate" style={{ gap: 6, fontSize: 11, color: 'var(--ink3)' }}>
                      <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: t.farbe, flexShrink: 0 }} />{t.label}
                    </span>
                    <span className="immo-zahl truncate" style={{ fontSize: 14, fontWeight: 800 }}>{eur(t.wert)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Widget>
      );
    }
    case 'kauf': {
      const k = kaufKennzahlen(e, miete.kalt);
      return (
        <Widget {...basis} icon={Bank} rechts={k.bank || undefined}>
          {!k.hatDaten ? <Leer text="Keine Kauf- oder Finanzierungsdaten erfasst." /> : (
            <div className="grid flex-1 content-center" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 10 }}>
              {[
                ['Kaufpreis', k.kaufpreis > 0 ? eur(k.kaufpreis, { nachkomma: 0 }) : '–'],
                ['Darlehen', k.darlehen > 0 ? eur(k.darlehen, { nachkomma: 0 }) : '–'],
                ['Bruttorendite', k.bruttorendite !== null ? `${k.bruttorendite.toLocaleString('de-DE')} %` : '–'],
              ].map(([l, v]) => (
                <div key={l} className="flex flex-col min-w-0" style={{ gap: 3 }}>
                  <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{l}</span>
                  <span className="immo-zahl truncate" style={{ fontFamily: RUND, fontSize: 18, fontWeight: 600 }}>{v}</span>
                </div>
              ))}
            </div>
          )}
        </Widget>
      );
    }
    case 'hausgeld': {
      const w = daten.eigentuemer;
      return (
        <Widget {...basis} icon={PiggyBank}>
          <WidgetWert wert={w?.hausgeld_vorschuss != null ? eur(w.hausgeld_vorschuss) : '–'}
            sub={w ? `pro Monat${w.mea != null ? ` · MEA ${w.mea.toLocaleString('de-DE')}` : ''}` : 'Kein Eigentümer-Eintrag'} />
        </Widget>
      );
    }
    case 'bank': {
      const sepa = aktuell?.sepa_aktiv === true;
      const iban = (aktuell?.mieter_iban ?? '').replace(/\s/g, '');
      return (
        <Widget {...basis} icon={Bank}>
          <WidgetWert wert={<span style={{ fontSize: 20 }}>{!aktuell ? '–' : sepa ? 'Lastschrift' : 'Überweisung'}</span>}
            sub={!aktuell ? 'Kein Mietverhältnis' : iban ? `IBAN ··· ${iban.slice(-4)}` : sepa ? 'IBAN fehlt' : 'Kein SEPA-Mandat'} />
        </Widget>
      );
    }
    case 'zaehler': {
      const liste = zaehlerDerEinheit(daten.zaehler, e.bezeichnung ?? '', daten.mieter.map(m => m.id)).filter(z => z.aktiv !== false);
      return (
        <Widget {...basis} icon={Gauge} rechts={`${liste.length} aktiv`}>
          {liste.length === 0 ? <Leer text="Keine Zähler dieser Einheit." /> : (
            <div className="flex flex-wrap content-center flex-1" style={{ gap: 6 }}>
              {liste.slice(0, 6).map(z => (
                <span key={z.id} className="flex items-center whitespace-nowrap" style={{ height: 28, padding: '0 10px', borderRadius: 14, gap: 6, fontSize: 12, fontWeight: 600, background: 'var(--fill2)' }}>
                  <Gauge weight="fill" size={13} color="var(--acc)" />{z.typ || 'Zähler'}{z.zaehler_nummer ? ` · ${z.zaehler_nummer}` : ''}
                </span>
              ))}
            </div>
          )}
        </Widget>
      );
    }
    case 'eig': {
      const eig = daten.eigentuemer?.eigentuemer;
      const name = eig ? kontaktName(eig) : daten.objektEigentuemer[0] ? kontaktName(daten.objektEigentuemer[0]) : '';
      return (
        <Widget {...basis} icon={CrownSimple}>
          {!name ? <Leer text="Kein Eigentümer hinterlegt." /> : (
            <div className="flex-1 flex flex-col items-center justify-center min-w-0" style={{ gap: 6 }}>
              <span className="flex items-center justify-center" style={{ width: 46, height: 46, borderRadius: '50%', background: 'var(--knob)', fontSize: 14, fontWeight: 600 }}>{initialen(name)}</span>
              <span className="truncate max-w-full" style={{ fontSize: 12, fontWeight: 700 }}>{name}</span>
              <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{eig ? 'Eigentümer der Einheit' : 'Eigentümer des Objekts'}</span>
            </div>
          )}
        </Widget>
      );
    }
    case 'notiz': {
      const letzte = daten.notizen[0];
      return (
        <Widget {...basis} icon={NotePencil} rechts={daten.notizen.length ? String(daten.notizen.length) : undefined}>
          {letzte ? <span className="flex-1 overflow-hidden" style={{ marginTop: 10, fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: 'var(--ink2)' }}>{letzte.text}</span>
            : <Leer text="Noch keine Notizen." />}
        </Widget>
      );
    }
    case 'docs':
      return (
        <Widget {...basis} icon={FolderSimple} rechts={`${daten.dokumente.length} Dateien`}>
          {daten.dokumente.length === 0 ? <Leer text="Noch keine Dokumente zu dieser Einheit." /> : (
            <div className="grid flex-1 items-center" style={{ gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 8 }}>
              {daten.dokumente.slice(0, 4).map(d => (
                <div key={d.id} className="flex flex-col items-center min-w-0" style={{ gap: 6 }}>
                  <span className="relative flex items-end justify-center" style={{ width: 40, height: 50, borderRadius: 8, paddingBottom: 6, background: 'linear-gradient(180deg,#f4f4f6,#d9d9de)', boxShadow: '0 4px 10px var(--shade)' }}>
                    <span style={{ fontSize: 8, fontWeight: 800, color: 'var(--bad)' }}>{(d.datei_name?.split('.').pop() ?? 'DOK').slice(0, 4).toUpperCase()}</span>
                  </span>
                  <span className="truncate max-w-full" style={{ fontSize: 10, fontWeight: 500, color: 'var(--ink2)' }}>{d.titel || d.datei_name || 'Dokument'}</span>
                </div>
              ))}
            </div>
          )}
        </Widget>
      );
    case 'konto':
      return (
        <Widget {...basis} icon={Wallet}>
          <Leer text={daten.rechnungen.length ? `${daten.rechnungen.length} ${daten.rechnungen.length === 1 ? 'Rechnung' : 'Rechnungen'} zur Einheit` : 'Soll, Ist und Saldo je Mietverhältnis'} />
          <Folgt text={BUCHHALTUNG_FOLGT} />
        </Widget>
      );
    // ── Mit der Buchhaltung bzw. ohne Datenquelle (Grundsteuer) ─────────
    case 'mahn': return <Widget {...basis} icon={EnvelopeSimpleOpen}><Folgt text={BUCHHALTUNG_FOLGT} /></Widget>;
    case 'mieterh': {
      if (!aktuell) return <Widget {...basis} icon={TrendUp}><Leer text="Leerstand." /></Widget>;
      const an = analyseMieterhoehung(aktuell, s.heute, s.flaeche);
      const moeglich = an.fruehestesVerlangen <= s.heute && an.spielraum > 0 && !an.geplanteAenderung;
      return (
        <Widget {...basis} icon={TrendUp}>
          <div className="flex flex-col" style={{ marginTop: 'auto', gap: 6 }}>
            <span className="immo-kpi" style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: moeglich ? 'var(--ok)' : 'var(--ink)' }}>
              {an.spielraum > 0 ? `+${eur(an.spielraum)}` : '–'}
            </span>
            <span><Chip groesse="xs" ton={moeglich ? 'ok' : an.spielraum > 0 ? 'warn' : 'neutral'}>
              {an.geplanteAenderung ? 'Mietstand geplant' : an.spielraum <= 0 ? 'Kappung erreicht' : moeglich ? 'Erhöhung möglich' : `ab ${deDatum(an.fruehestesVerlangen)}`}
            </Chip></span>
            <span className="truncate" style={{ fontSize: 11, color: 'var(--ink3)' }}>{an.kaltProQm !== null ? `${eur(an.kaltProQm)} / m² · Kappung 20 %` : 'Kappung 20 %'}</span>
          </div>
        </Widget>
      );
    }
    case 'keys': {
      const st = schluesselJeEinheit(daten.uebergaben).get(e.id);
      return (
        <Widget {...basis} icon={Key}>
          <WidgetWert wert={st ? st.summe : '–'} sub={!st ? 'Noch keine Übergabe' : st.art === 'einzug' ? `bei ${aktuell && st.mieterId === aktuell.id ? kontaktName(aktuell) : 'Mieter'}` : `Auszug am ${deDatum(st.datum)}`} />
        </Widget>
      );
    }
    case 'grund': return <Widget {...basis} icon={MapPinArea}><Folgt /></Widget>;
    case 'wartung': {
      const naechste = daten.termine.find(t => t.typ === 'wartung' && t.start_datum.slice(0, 10) >= s.heute && (t.status ?? '') !== 'abgesagt');
      const letzte = daten.objekt.letzte_pruefung ?? null;
      return (
        <Widget {...basis} icon={ShieldCheck}>
          <WidgetWert wert={<span style={{ fontSize: 20 }}>{naechste ? deDatum(naechste.start_datum) : letzte ? deDatum(letzte) : '–'}</span>}
            sub={naechste ? 'nächste Wartung' : letzte ? 'letzte Prüfung (Objekt)' : 'Keine Prüfung erfasst'} />
        </Widget>
      );
    }
    case 'vertraege':
      return (
        <Widget {...basis} icon={Signature}>
          <WidgetWert wert={daten.vertraege.length} sub={`Mietverträge · ${daten.dienstleister.length} Dienstleister (Objekt)`} />
        </Widget>
      );
    case 'versich': {
      const n = daten.fachDokumente.filter(d => d.kategorie === 'Versicherung').length;
      return <Widget {...basis} icon={Umbrella}><WidgetWert wert={n} sub="Dokumente (Objekt)" /></Widget>;
    }
    case 'tickets': {
      const offen = daten.tickets.filter(t => istTicketOffen(t.status));
      return (
        <Widget {...basis} icon={Ticket} rechts={`${offen.length} offen`}>
          {offen.length === 0 ? <Leer text="Keine offenen Tickets." /> : (
            <div className="flex flex-col flex-1 justify-center" style={{ gap: 6 }}>
              {offen.slice(0, 3).map(t => (
                <div key={t.id} className="flex items-center min-w-0" style={{ gap: 8, fontSize: 13 }}>
                  <Ticket weight="fill" size={14} color={t.eskaliert || t.status === 'eskaliert' ? 'var(--bad)' : 'var(--acc)'} />
                  <span className="truncate flex-1" style={{ fontWeight: 600 }}>{t.titel || 'Ticket'}</span>
                  <span className="shrink-0" style={{ fontSize: 11, color: 'var(--ink3)' }}>{ticketStatusLabel(t.status)}</span>
                </div>
              ))}
            </div>
          )}
        </Widget>
      );
    }
    case 'etv': {
      const naechste = daten.termine.find(t => t.typ === 'meeting' && t.start_datum.slice(0, 10) >= s.heute && (t.status ?? '') !== 'abgesagt');
      return <Widget {...basis} icon={UsersThree}><WidgetWert wert={<span style={{ fontSize: 20 }}>{naechste ? deDatum(naechste.start_datum) : '–'}</span>} sub={naechste ? 'nächste Versammlung' : 'Keine geplant'} /></Widget>;
    }
    case 'protokoll': {
      const liste = baueAktivitaeten({
        notizen: daten.notizen, dokumente: daten.dokumente, tickets: daten.tickets, uebergaben: daten.uebergaben,
        mieter: daten.mieter.map(m => ({ id: m.id, name: kontaktName(m), mietbeginn: m.mietbeginn, mietende: m.mietende })),
      }, new Date().toISOString(), 3);
      return (
        <Widget {...basis} icon={ClockCounterClockwise}>
          {liste.length === 0 ? <Leer text="Noch keine Aktivitäten." /> : (
            <div className="flex flex-col flex-1 justify-center" style={{ gap: 6 }}>
              {liste.map((x, i) => (
                <div key={`${x.id ?? i}`} className="flex items-center min-w-0" style={{ gap: 8, fontSize: 13 }}>
                  <span className="shrink-0 immo-zahl" style={{ fontSize: 11, color: 'var(--ink3)', width: 70 }}>{deDatum(x.datum)}</span>
                  <span className="truncate flex-1" style={{ fontWeight: 600 }}>{x.titel}</span>
                </div>
              ))}
            </div>
          )}
        </Widget>
      );
    }
    default: return null;
  }
}

function MieterWidget({ basis, gesperrt }: { basis: Pick<WidgetProps, 'titel' | 'groesse' | 'onOeffnen' | 'style'>; gesperrt: boolean }) {
  const s = useEinheitSeite();
  const m = s.aktuell;
  const portal = usePortalStatus(m?.email);
  if (!m) {
    return (
      <Widget {...basis} icon={User}>
        <div className="flex flex-1 items-center" style={{ gap: 14 }}>
          <span className="flex items-center justify-center shrink-0" style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--warn-soft)' }}><User weight="fill" size={24} color="var(--warn)" /></span>
          <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
            <span style={{ fontSize: 20, fontWeight: 800 }}>Leerstand</span>
            <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Kein laufendes Mietverhältnis</span>
          </div>
        </div>
        <div className="flex" style={{ gap: 8 }}>
          <WidgetKnopf icon={User} deaktiviert={gesperrt} onClick={() => s.oeffneBereich('mv')}>Mieter zuordnen</WidgetKnopf>
        </div>
      </Widget>
    );
  }
  const name = kontaktName(m);
  const telefon = (m.mobil ?? '').trim() || (m.telefon ?? '').trim();
  const email = (m.email ?? '').trim();
  const personen = m.anzahl_bewohner ?? (1 + s.daten.mitmieter.filter(x => x.hauptId === m.id).length);
  return (
    <Widget {...basis} icon={User} rechts={m.mietbeginn ? `seit ${deDatum(m.mietbeginn)}` : undefined}>
      <div className="flex flex-1 items-center min-w-0" style={{ gap: 14 }}>
        <span className="flex items-center justify-center shrink-0" style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--acc-fill)', color: 'var(--acc-ink)', fontSize: 18, fontWeight: 600 }}>{initialen(name)}</span>
        <div className="flex flex-col min-w-0" style={{ gap: 2 }}>
          <span className="truncate" style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em' }}>{name}</span>
          <span className="truncate" style={{ fontSize: 13, color: 'var(--ink3)' }}>
            {m.mietbeginn ? `Mieter seit ${deDatum(m.mietbeginn)}` : 'Mieter'}{personen > 0 ? ` · ${personen} ${personen === 1 ? 'Person' : 'Personen'}` : ''}
          </span>
        </div>
        {portal.status === 'aktiv' && <span className="ml-auto shrink-0"><Chip groesse="xs" ton="ok">Portal aktiv</Chip></span>}
        {portal.status === 'gesperrt' && <span className="ml-auto shrink-0"><Chip groesse="xs" ton="bad">Portal gesperrt</Chip></span>}
      </div>
      <div className="flex" style={{ gap: 8 }}>
        <WidgetKnopf icon={Phone} ton="ok" href={telefon ? `tel:${telefon.replace(/[^\d+]/g, '')}` : undefined} deaktiviert={gesperrt || !telefon} titel={telefon || 'Keine Telefonnummer hinterlegt'}>Anrufen</WidgetKnopf>
        <WidgetKnopf icon={EnvelopeSimple} href={email ? `mailto:${email}` : undefined} deaktiviert={gesperrt || !email} titel={email || 'Keine E-Mail hinterlegt'}>E-Mail</WidgetKnopf>
        <WidgetKnopf icon={ChatCircle} deaktiviert={gesperrt} onClick={() => s.oeffneBereich('mieter')}>{portal.status === 'aktiv' ? 'Portal' : 'Freigeben'}</WidgetKnopf>
      </div>
    </Widget>
  );
}
